import React from "react";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { Seo } from "../components/Seo";

const articles = [
  {
    title: "The Tuesday Test: Will Your AI Idea Survive an Ordinary Workday?",
    blurb: "A useful AI application has to fit the pace, interruptions, responsibilities, and habits of the people expected to use it.",
  },
  {
    title: "Roots Before Fruits: The Human Work Behind AI Adoption",
    blurb: "Why clear expectations, trust, and everyday leadership influence the success of new technology.",
  },
  {
    title: "Where Did the Afternoon Go?",
    blurb: "A closer look at repetitive administrative tasks, troublesome handoffs, and how small service businesses can find opportunities for improvement.",
  },
];

export default function FieldNotes() {
  return (
    <div data-testid="page-field-notes">
      <Seo
        title="Field Notes | SymbiCognitive"
        description="Field Notes — practical thinking about operations, AI adoption, leadership, and organizational culture for smaller businesses."
        path="/field-notes"
      />
      <section className="bg-midnight pt-20" data-testid="field-notes-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Field Notes</span>
          <h1 className="font-display font-extrabold text-white text-4xl sm:text-5xl tracking-tight mt-4 max-w-3xl leading-[1.05]">Thinking out loud about better work.</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            AI is changing quickly. The everyday challenges of leading people, serving customers, and running a business remain familiar. Field Notes is where I explore the intersection of technology, operations, leadership, and organizational culture — with particular attention to the opportunities and challenges facing smaller businesses.
          </p>
          <p className="mt-4 text-slate-400 max-w-2xl">
            Expect practical examples, useful questions, occasional experiments, and a healthy respect for what happens when a promising idea meets a busy Tuesday.
          </p>
        </div>
        <div className="gradient-divider h-1.5 w-full" />
      </section>

      <section className="bg-white py-24 sm:py-28" data-testid="field-notes-list">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="flex items-center gap-3 mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">In the works</span>
          </div>
          <div className="space-y-5">
            {articles.map((a, i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`article-${i}`}
                className="rounded-2xl border border-slate-200 p-7 sm:p-8"
              >
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-violet bg-violet/10 px-3 py-1 rounded-full">
                  <Clock size={13} /> Coming soon
                </span>
                <h2 className="mt-4 font-display font-semibold text-slateink text-xl sm:text-2xl leading-snug">{a.title}</h2>
                <p className="mt-2 text-[var(--slate-soft)] leading-relaxed">{a.blurb}</p>
              </motion.article>
            ))}
          </div>
          <p className="mt-12 text-center text-sm text-slate-400">
            The first notes are being written now. Check back soon — or{" "}
            <a href="/contact" className="text-violet font-medium hover:underline">drop a note</a> if a topic is on your mind.
          </p>
        </div>
      </section>
    </div>
  );
}
